import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  editCandidate,
  editCandidateLanguageProficiency,
  editCandidateSkills,
  getCandidateById,
  getCandidateFiles,
  getCandidateSkills,
  uploadCandidateFile,
} from '../Services/Candidates';
import { CandidateSchema, englishJobCandidateSchema } from '../Utils/schemas';
import { englishFields } from '../Utils/fields';
import { DocumentType } from '../Types/constants';

export const useCandidate = (defaultTab: number) => {
  const { candidateId: id } = useParams();
  const queryClient = useQueryClient();

  const [onEdit, setOnEdit] = useState<boolean>(false);
  const [candidate, setCandidate] = useState<Candidate>();
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [loadingFiles, setIsLoadingFiles] = useState(false);
  const [languageEdit, setLanguageEdit] = useState<boolean>(false);

  const { data: candidateData } = useQuery({
    queryKey: ['getCandidateById', id],
    queryFn: () => getCandidateById(id),
    enabled: !!id,
  });

  const { data: candidateSkills } = useQuery({
    queryKey: ['getCandidateSkills', id],
    queryFn: () => getCandidateSkills(Number(id)),
  });

  const { mutate: editCandidateMutation } = useMutation({
    mutationFn: (data: Candidate) => editCandidate(id, data),
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
    },
    onSuccess: () => {
      toast.success(t('toast.success.edit', { elem: 'Candidate' }));
      setOnEdit(false);
      queryClient.invalidateQueries({ queryKey: ['getCandidateById'] });
    },
  });

  const { mutate: editSkillsMutation } = useMutation({
    mutationFn: (skills: string[]) => editCandidateSkills(id, skills),
    onSuccess: () => {
      toast.success(t('toast.success.edit', { elem: 'Skills' }));
      queryClient.invalidateQueries({ queryKey: ['getCandidateSkills', id] });
    },
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
    },
  });

  const { data: candidateFiles, isLoading: isLoadingGetFiles } = useQuery({
    queryKey: ['getCandidateFiles'],
    queryFn: () =>
      getCandidateFiles(id!, {
        page: 1,
        count: 100,
      }),
    enabled: !!id,
  });

  const { mutate: uploadCandidateFileMutation } = useMutation({
    mutationFn: (data: { formData: FormData; documentType?: number }) => 
      uploadCandidateFile(id, data.formData, data.documentType),
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
      setIsLoadingFiles(false);
    },
    onSuccess: () => {
      toast.success(t('toast.success.upload'));
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: ['getCandidateFiles'] });
      setIsLoadingFiles(false);
    },
  });

  const handleFileUpload = () => {
    if (files.length === 0) return;

    setIsLoadingFiles(true);
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    uploadCandidateFileMutation({ formData });
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof CandidateSchema>>({
    resolver: zodResolver(CandidateSchema),
  });

  useEffect(() => {
    if (candidate) {
      reset(candidate);
    }
  }, [candidate, reset]);

  useEffect(() => {
    if (candidateData) {
      const updatedCandidate = {
        ...candidateData,
        lastContactDateTime: candidateData.lastContactDateTime?.split('T')[0] ?? null,
      };
      setCandidate(updatedCandidate);
    }
  }, [candidateData]);

  useEffect(() => {
    if (candidateSkills) {
      setSelectedSkills(candidateSkills.skills);
    }
  }, [candidateSkills]);

  const onSubmit = (data: z.infer<typeof CandidateSchema>) => {
    const editedCandidate: Candidate = {
      fullName: data.fullName,
      email: data.email,
      phone: data.phone ?? '',
      city: data.city ?? '',
      linkedin: data.linkedin ?? '',
      lastContactDateTime: data.lastContactDateTime ?? null,
      address: data.address ?? '',
      referralId: data.referral?.id ? Number(data.referral.id) : undefined,
      referralDetail: data.referralDetail ?? '',
      areaId: data.area?.id ? Number(data.area.id) : undefined,
      countryId: data.country?.id ? Number(data.country.id) : undefined,
      languageProficiencyData: {
        level: '',
        testDate: '',
        testResult: '',
        comments: '',
        otherLanguages: ''
      },
    };
    editCandidateMutation(editedCandidate);
  };

  const { mutate: editLanguageMutation } = useMutation({
    mutationFn: (languageProficiencyData: LanguageProficiencyDataOut) => editCandidateLanguageProficiency(id, languageProficiencyData),
    onSuccess: () => {
      toast.success(t('toast.success.edit', { elem: 'English Proficiency' }));
      queryClient.invalidateQueries({ queryKey: ['getCandidateById'] });
    },
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
    },
  });

  const {
    control: englishControl,
    handleSubmit: englishHandleSubmit,
    reset: englishReset,
    formState: { errors: englishErrors },
  } = useForm<z.infer<typeof englishJobCandidateSchema>>({
    resolver: zodResolver(englishJobCandidateSchema),
    defaultValues: {
      level: '',
      testDate: '',
      testResult: '',
      comments: '',
      otherLanguages: '',
    },
  });

  useEffect(() => {
    if (candidate?.languageProficiencyData && !languageEdit) {
      const testDateValue = candidate.languageProficiencyData.testDate;
      let formattedTestDate = '';
      if (testDateValue) {
        try {
          formattedTestDate = testDateValue.includes('T') 
            ? testDateValue.split('T')[0] 
            : testDateValue;
        } catch {
          formattedTestDate = testDateValue;
        }
      }
      
      englishReset({
        level: candidate.languageProficiencyData.level || '',
        testDate: formattedTestDate,
        testResult: candidate.languageProficiencyData.testResult || '',
        comments: candidate.languageProficiencyData.comments || '',
        otherLanguages: candidate.languageProficiencyData.otherLanguages || '',
      });
    }
  }, [candidate?.languageProficiencyData, languageEdit, englishReset]);

  const languageFormData = {
    fields: englishFields,
    control: englishControl,
    values: candidate?.languageProficiencyData ? {
      ...candidate.languageProficiencyData,
      attachments: candidateFiles?.filter(file => file.documentType === DocumentType.EnglishDocument) || [],
    } : {
      level: '',
      testDate: '',
      testResult: '',
      comments: '',
      otherLanguages: '',
      attachments: [],
    },
    handleSave: () =>
      englishHandleSubmit((data) => {
        const editedLanguageProficiencyData: LanguageProficiencyDataOut = {
          level: data.level ?? '',
          testDate: data.testDate ?? '',
          testResult: data.testResult ?? '',
          comments: data.comments ?? '',
          otherLanguages: data.otherLanguages ?? '',
        };
        editLanguageMutation(editedLanguageProficiencyData);
        setLanguageEdit(false);
      })(),
    handleEdit: () => setLanguageEdit(true),
    onEdit: languageEdit,
    handleCancel: () => {
      if (candidate?.languageProficiencyData) {
        const testDateValue = candidate.languageProficiencyData.testDate;
        let formattedTestDate = '';
        if (testDateValue) {
          try {
            formattedTestDate = testDateValue.includes('T') 
              ? testDateValue.split('T')[0] 
              : testDateValue;
          } catch {
            formattedTestDate = testDateValue;
          }
        }
        
        englishReset({
          level: candidate.languageProficiencyData.level || '',
          testDate: formattedTestDate,
          testResult: candidate.languageProficiencyData.testResult || '',
          comments: candidate.languageProficiencyData.comments || '',
          otherLanguages: candidate.languageProficiencyData.otherLanguages || '',
        });
      }
      setLanguageEdit(false);
    },
    errors: englishErrors,
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return {
    onEdit,
    setOnEdit,
    candidate,
    selectedTab,
    control,
    handleSubmit,
    errors,
    onSubmit,
    handleChange,
    selectedSkills,
    handleSkillsSave: editSkillsMutation,
    candidateFiles,
    files,
    setFiles,
    handleFileUpload,
    loadingFiles,
    isLoadingGetFiles,
    setValue,
    languageFormData,
  };
};
